/**
 * THE DOOR — the phone approves this browser, and this browser gets a bearer.
 *
 * IT REPLACES A PASSWORD FORM rather than standing beside one, and that is the decision. A form on the
 * Arena origin is the second sign-in screen `shared/coord/bearer.ts` argues must not exist, and the QR is
 * not a convenience for whoever prefers their PC: it is the way in, and there is no other. A convenience
 * beside a form leaves the form as the real door.
 *
 * WHY IT TALKS TO THE BACKEND AND NOT TO THIS SERVICE. The phone's approval screen is the anti-quishing
 * control (`.ai/auth/qr-login.md §4`): it shows the human WHO IS ASKING, and it can only do that if the
 * party minting the ticket is looking at the asking browser. Proxying these three calls through the Arena
 * service would have made every ticket describe the service. The cost is that the ticket secret lives in
 * this page's `sessionStorage` — argued where it is paid, in `ArenaDoorController`'s docblock.
 *
 * WHAT IT IS NOT: a second place where identity is decided. The claim hands back the callsign and the
 * group the backend already holds (`App\Support\ArenaIdentity`), and this page passes them on without
 * having an opinion (`arena.md §C`).
 */

/** Where the approval stands while the human works. Anything else is a failure carrying its reason. */
export type DoorPhase = 'asking' | 'waiting' | 'approved' | 'expired' | 'denied' | 'unknown';

/**
 * A DOOR FAILURE CARRIES ITS KEY, never its sentence.
 *
 * This module sits behind a page drawn in whatever language the browser asked for, so a sentence written
 * here is one that cannot be translated — and the door is where an unreadable failure means somebody
 * cannot play and does not know why. `detail` is for the log, which is an instrument and stays English.
 * `check:i18n` reads the key out of the constructor and refuses a `new Error` with a literal in this page.
 */
export class DoorError extends Error {
  // `waitS` is SECONDS TO WAIT, or `null` when trying again cannot change the answer. Whether there is
  // a next move, and when, is a property of the CAUSE and not of the screen (`code-quality.md §12.2`).
  constructor(
    readonly key: string,
    readonly vars: Record<string, string | number> = {},
    detail = '',
    readonly waitS: number | null = null,
  ) {
    super(detail ? `${key} — ${detail}` : key);
    this.name = 'DoorError';
  }
}

export type DoorIdentity = { callsign: string; team: { tag: string; name: string } | null };

type Ticket = { id: string; secret: string; svg: string; code: string; expires_in: number; poll_interval_ms: number };

/**
 * WHERE THE TICKET LIVES BETWEEN A RELOAD AND ITS CLAIM.
 *
 * `sessionStorage`, the same store and the same reasoning as the bearer it becomes (T1): a ticket that
 * outlived its tab would be a live approval waiting in a browser nobody is watching. The tab IS the
 * session here, and a rotation replaces the entry rather than adding one.
 */
const TICKET_KEY = 'arena-door-ticket';

function holdTicket(t: Ticket | null): void {
  try {
    if (t) sessionStorage.setItem(TICKET_KEY, JSON.stringify({ id: t.id, secret: t.secret }));
    else sessionStorage.removeItem(TICKET_KEY);
  } catch {
    // Storage denied: the ticket lives for this page load only, which is one rotation shorter and not
    // broken. The alternative — refusing to open the door at all — is worse.
  }
}

/**
 * A REQUEST THAT NEVER LANDED IS ITS OWN ANSWER, not an unknown one (`code-quality.md §12.2`).
 *
 * `fetch` rejects for one class of reason — nothing came back — and that has a way out a player can act
 * on. Left to the caller it arrives as a thrown `TypeError` beside five refusals that all mean the
 * opposite: the service answered and said no.
 */
async function ask(apiBase: string, path: string, body?: unknown): Promise<Response> {
  try {
    return await fetch(`${apiBase}/v1/arena/door${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
  } catch (err) {
    throw new DoorError('door.err_offline', {}, String(err));
  }
}

/** Seconds the service asked to be left alone for. Zero when it did not say, which reads as "no hint". */
const retryAfter = (res: Response): number => Math.max(0, Math.trunc(Number(res.headers.get('retry-after')) || 0));

/**
 * WHEN A REFUSAL IS A "NOT NOW" — one rule, read by every call this page makes.
 *
 * A service answering 429 or 503 AND saying for how long is asking to be left alone, and the wait is
 * what a page can act on. One that will not say has given a refusal nobody can time, so it stays an
 * ordinary failure naming its status: counting down from a number nobody sent is worse than not
 * counting (`arena.md §D.8`). Both emitters here do say — the plane's throttle and the service's gate.
 */
function notNow(res: Response, detail = ''): DoorError | null {
  const s = retryAfter(res);
  if (!s) return null;
  const why = detail || `retry-after ${s}`;
  if (res.status === 429) return new DoorError('door.err_too_many', { s }, why, s);
  if (res.status === 503) return new DoorError('door.err_busy', { s }, why, s);
  return null;
}

/** Mint a ticket. Throws with a readable reason: the door is the surface where a failure must be legible. */
export async function openTicket(apiBase: string): Promise<Ticket> {
  const res = await ask(apiBase, '');
  if (res.status === 404) throw new DoorError('door.err_off', {}, 'weblogin.enabled is off on this backend');
  if (!res.ok) throw notNow(res) ?? new DoorError('door.err_ticket', { code: res.status });
  const t = (await res.json()) as Ticket;
  holdTicket(t);
  return t;
}

/**
 * Where the approval stands. Never throws for a dead ticket: `expired` is an ANSWER and the caller has a
 * next move for it (rotate), where an exception would just be a stack trace on the way in.
 */
export async function pollTicket(apiBase: string, t: Ticket): Promise<DoorPhase> {
  const res = await ask(apiBase, `/${t.id}/status`, { secret: t.secret });
  // A ticket this page could not ASK about is not a dead one. Read as `expired` it rotates, minting a
  // second ticket against the same refusal — which is how a throttle turns into a storm.
  if (!res.ok) return notNow(res) ? 'unknown' : 'expired';
  const status = String(((await res.json()) as { status?: unknown }).status ?? '');
  if (status === 'approved') return 'approved';
  if (status === 'denied') return 'denied';
  if (status === 'pending') return 'waiting';
  return 'expired';
}

/**
 * Turn an approved ticket into a bearer.
 *
 * Single use on the backend's side, so this consumes the held ticket whatever happens — a ticket kept
 * after its claim is one this page would keep polling for an answer that can never change.
 */
export async function claimTicket(apiBase: string, t: Ticket): Promise<{ token: string; identity: DoorIdentity }> {
  const res = await ask(apiBase, `/${t.id}/claim`, { secret: t.secret });
  // A CLAIM THE SERVICE COULD NOT ANSWER LEAVES THE TICKET ALIVE. The approval is single use and a
  // human already gave it, so dropping it here spends something nobody can get back without them.
  const later = res.ok ? null : notNow(res);
  if (later) throw later;
  holdTicket(null);
  if (!res.ok) {
    const why = String(((await res.json().catch(() => ({}))) as { error?: unknown }).error ?? res.status);
    throw new DoorError('door.err_claim', { why });
  }
  const body = (await res.json()) as { token: string; identity: DoorIdentity };
  return body;
}

/**
 * THE THREE ANSWERS THE DOOR CAN GET, and they are three because the NEXT MOVE is three.
 *
 * `refused` means this credential will not open it however often it is presented, so a fresh QR is the
 * cure. `later` means the service could not decide — a new QR fails the same way, and the advice to mint
 * one is the OPPOSITE of what to do. Collapsing them was `code-quality.md §12.2` with the door as object.
 */
export type GateAnswer =
  | { kind: 'admitted'; identity: DoorIdentity | null }
  | { kind: 'refused'; why: string }
  | { kind: 'later'; err: DoorError };

/**
 * TELL THIS SERVICE WHO CAME IN — the step that turns a bearer into permission to receive the game.
 *
 * A SEPARATE CALL AND NOT SOMETHING THE PAGE CAN SKIP, because it is the lock. The bearer alone opens
 * the coordination plane, which is Laravel; what opens the SERVICE — the client bytes and the socket — is
 * the cookie this sets, and only the service can set it (`server/http/gate.ts`).
 *
 * The identity that comes back is the one the SERVICE verified, which may differ from the one the claim
 * reported if the bearer changed hands in between. The service's answer is the one the game is played under.
 */
export async function presentAtGate(bearerToken: string): Promise<GateAnswer> {
  let res: Response;
  try {
    res = await fetch('./gate', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      // Same-origin, so the cookie the service sets comes back on the navigation that follows.
      credentials: 'same-origin',
      body: JSON.stringify({ bearer: bearerToken }),
    });
  } catch (err) {
    return { kind: 'later', err: new DoorError('door.err_gate_down', {}, String(err)) };
  }
  if (res.ok) return { kind: 'admitted', identity: ((await res.json()) as { identity?: DoorIdentity }).identity ?? null };
  const why = String(((await res.json().catch(() => ({}))) as { error?: unknown }).error ?? res.status);
  // 401 and 403 are the same MOVE — this credential is not the one — and the door says so by drawing a
  // fresh code. The two reasons stay apart in the log, which is where the difference is diagnosed.
  if (res.status === 401 || res.status === 403) return { kind: 'refused', why };
  const later = notNow(res, why);
  if (later) return { kind: 'later', err: later };
  return { kind: 'later', err: new DoorError('door.err_gate', { code: res.status }, why) };
}

/**
 * THE WAY IN THAT COSTS NO PHONE, when this deployment names a string (`server/http/gate.ts`).
 *
 * It ends where the door ends — a pass this service signed — and starts nowhere near the plane: there is
 * no account, so there is no bearer and nothing to remember. `false` is a refusal that a fresh code CAN
 * cure, so the caller falls back to the QR rather than stopping.
 */
export async function presentDemo(token: string): Promise<GateAnswer> {
  let res: Response;
  try {
    res = await fetch('./gate/demo', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ token }),
    });
  } catch (err) {
    return { kind: 'later', err: new DoorError('door.err_gate_down', {}, String(err)) };
  }
  if (res.ok) return { kind: 'admitted', identity: ((await res.json()) as { identity?: DoorIdentity }).identity ?? null };
  const why = String(((await res.json().catch(() => ({}))) as { error?: unknown }).error ?? res.status);
  if (res.status === 401 || res.status === 403 || res.status === 404) return { kind: 'refused', why };
  const later = notNow(res, why);
  return { kind: 'later', err: later ?? new DoorError('door.err_gate', { code: res.status }, why) };
}

/**
 * Whether this deployment locks the game at all.
 *
 * Asked rather than assumed: the gate defaults OFF so every harness in this repo keeps working
 * (`server/http/gate.ts`), and a door page that insisted on a handshake an unlocked service never
 * performs would be a door in front of an open field.
 */
export async function gateIsUp(): Promise<boolean> {
  try {
    const res = await fetch('./gate/me', { credentials: 'same-origin' });
    /**
     * IT READS THE STATE AND NOT THE STATUS CODE (T7). This used to answer `401 || 200`, and measured
     * on the `:8790` with the lock OFF the route replied 401 to everybody — so this said *"there is a
     * gate"* on a service that has none, and put a door in front of an open field. `enabled` is the fact;
     * the status is what the fact does to THIS browser.
     */
    if (res.status === 401) return true;
    if (!res.ok) return false;
    return ((await res.json()) as { enabled?: boolean }).enabled === true;
  } catch {
    return false;
  }
}
